import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Code2, Rocket, Server, Gauge, Zap, Shield, Cpu, Layout, Smartphone, Layers, Workflow, ExternalLink, Github, CheckCircle2 } from "lucide-react";
import { TechStackGrid } from "./TechStackGrid";
import { Button } from "./ui/button";
import ProceduralGroundBackground from "./ui/procedural-ground-background";

const devServices = [
  {
    icon: Layout,
    title: "Frontend Development",
    description: "Interfaces modernas e responsivas com as melhores práticas de UX/UI",
    techs: ["React", "Next.js", "TypeScript", "Tailwind CSS"]
  },
  {
    icon: Server,
    title: "Backend Development",
    description: "APIs robustas e escaláveis com arquitetura de microsserviços",
    techs: ["Node.js", "Python", "PostgreSQL", "Redis"]
  },
  {
    icon: Smartphone,
    title: "Mobile & PWA",
    description: "Aplicativos mobile e Progressive Web Apps performáticos",
    techs: ["React Native", "PWA", "iOS", "Android"]
  },
  {
    icon: Layers,
    title: "Cloud & DevOps",
    description: "Infraestrutura como código e automação de deployments",
    techs: ["AWS", "Docker", "Kubernetes", "CI/CD"]
  },
];

const devProjects = [
  {
    id: "1",
    title: "Sistema de Gestão",
    description: "ERP completo para gestão empresarial com controle de estoque, financeiro e RH",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop",
    stack: ["React", "Node.js", "PostgreSQL"],
    features: ["Gesto de estoque", "Controle financeiro", "Dashboard em tempo real", "Multi-usuário"],
    link: "/desenvolvimento",
  },
  {
    id: "2",
    title: "E-commerce Platform",
    description: "Plataforma de vendas online completa com carrinho, checkout e integrações de pagamento",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=500&fit=crop",
    stack: ["Next.js", "Supabase", "Stripe"],
    features: ["Catálogo dinâmico", "Carrinho persistente", "Checkout seguro", "Painel admin"],
    link: "/desenvolvimento",
  },
  {
    id: "3",
    title: "Dashboard Analytics",
    description: "Painel de métricas em tempo real com visualização de dados avançada",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop",
    stack: ["React", "Python", "AWS"],
    features: ["Gráficos interativos", "Alertas automatizados", "Exportação de dados", "API REST"],
    link: "/desenvolvimento",
  },
];

const metrics = [
  { icon: Code2, value: "50+", label: "Projetos Entregues", description: "Sistemas em produção" },
  { icon: Rocket, value: "99.9%", label: "Uptime Garantido", description: "Disponibilidade 24/7" },
  { icon: Server, value: "<1s", label: "Tempo de Resposta", description: "Performance otimizada" },
  { icon: Gauge, value: "100%", label: "Satisfação", description: "Clientes satisfeitos" },
];

const whyChoose = [
  {
    icon: Zap,
    title: "Performance First",
    description: "Código otimizado para máxima performance e SEO"
  },
  {
    icon: Shield,
    title: "Segurança Avançada",
    description: "Implementação de melhores práticas de segurança"
  },
  {
    icon: Cpu,
    title: "Escalável",
    description: "Arquitetura preparada para crescer com seu negócio"
  },
];

export const DevSection = () => {
  return (
    <section className="section-padding relative overflow-hidden">
      <ProceduralGroundBackground />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 text-sm font-medium glassmorphism-card text-blue-400 rounded-full"
          >
            <Code2 className="w-4 h-4" />
            Desenvolvimento Fullstack
          </motion.div>

          <h2 className="heading-section mb-4">
            Do Conceito ao{" "}
            <span className="gradient-text-primary">
              Deploy
            </span>
          </h2>
          <p className="text-body-lg text-muted-foreground max-w-3xl mx-auto">
            Desenvolvimento web completo com as tecnologias mais modernas do mercado.
            Sistemas robustos, escaláveis e com código limpo que geram valor real para o seu negócio.
          </p>
        </motion.div>

        {/* Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
        >
          {metrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="glassmorphism-card rounded-2xl p-8 text-center card-hover-effect h-full">
                <metric.icon className="h-10 w-10 text-blue-400 mx-auto mb-4" />
                <div className="text-4xl font-bold mb-2 gradient-text-primary">
                  {metric.value}
                </div>
                <div className="heading-card-sm text-white mb-2">{metric.label}</div>
                <p className="text-sm text-muted-foreground">{metric.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Tech Stack */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mb-20 bg-muted/20 rounded-3xl p-8 md:p-12"
        >
          <div className="text-center mb-12">
            <h3 className="heading-section mb-4">
              Stack de <span className="gradient-text-primary">Tecnologias</span>
            </h3>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Domínio completo das tecnologias mais demandadas do mercado
            </p>
          </div>
          <TechStackGrid showCategories />
        </motion.div>

        {/* Services */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="heading-section mb-4">
              O Que <span className="gradient-text-primary">Entrego</span>
            </h3>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Soluções completas de desenvolvimento sob medida para suas necessidades
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {devServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="glassmorphism-card rounded-2xl p-8 h-full card-hover-effect">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-xl mb-6 group-hover:scale-110 transition-transform">
                    <service.icon className="h-7 w-7 text-blue-400" />
                  </div>
                  <h4 className="heading-card mb-3 text-white">{service.title}</h4>
                  <p className="text-muted-foreground mb-6 text-sm">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.techs.map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 text-xs font-medium bg-blue-500/10 text-blue-400 rounded-full border border-blue-500/20"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="heading-section mb-4">
              Projetos em <span className="gradient-text-primary">Destaque</span>
            </h3>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Sistemas completos desenvolvidos com foco em performance e experiência do usuário
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {devProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="glassmorphism-card rounded-2xl overflow-hidden h-full card-hover-effect">
                  <div className="aspect-video overflow-hidden relative">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                  </div>

                  <div className="p-8">
                    <h4 className="heading-card-lg mb-3 text-white">{project.title}</h4>
                    <p className="text-muted-foreground mb-6">{project.description}</p>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.stack.map((tech) => (
                        <span
                          key={tech}
                          className="px-3 py-1.5 text-xs font-medium bg-indigo-500/10 text-indigo-400 rounded-lg border border-indigo-500/20"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-6">
                      {project.features.slice(0, 4).map((feature) => (
                        <div key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <Button size="sm" className="liquid-glass-btn-solid flex-1" asChild>
                        <Link to={project.link} className="flex items-center justify-center">
                          Ver Detalhes
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                      <Button size="sm" variant="outline" className="liquid-glass-outline flex-1" asChild>
                        <a href="#" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Why Choose */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="heading-section mb-4">
              Por Que <span className="gradient-text-primary">Escolher</span>
            </h3>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Diferenciais que garantem a qualidade e o sucesso do seu projeto
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyChoose.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="glassmorphism-card rounded-2xl p-8 h-full">
                  <item.icon className="h-10 w-10 text-indigo-400 mx-auto mb-4" />
                  <h4 className="heading-card-sm mb-3 text-white">{item.title}</h4>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <div className="glassmorphism-card rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/10 blur-[100px] rounded-full" />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative z-10"
          >
            <Workflow className="h-12 w-12 text-blue-400 mx-auto mb-6" />
            <h2 className="heading-section mb-6 text-white">
              Precisa de um <span className="gradient-text-primary">Sistema Web</span>?
            </h2>
            <p className="text-body-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
              Vamos conversar sobre como posso ajudar a transformar sua ideia em um sistema funcional,
              escalável e que gera valor real para o seu negócio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="liquid-glass-btn-solid rounded-full px-8 h-14 text-lg" asChild>
                <Link to="/contato">
                  Solicitar Orçamento
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="liquid-glass-outline rounded-full px-8 h-14 text-lg" asChild>
                <Link to="/desenvolvimento">
                  Ver Projetos
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DevSection;
